import { MB_SVG_Plus, MB_SVG_X } from '@/components/general/svg-components';
import { mbApplyTransparency, mbPlatformStyle } from '@/utils/general/style-utils';
import React, { ReactElement, useEffect, useRef, useState, useImperativeHandle, useCallback, useMemo } from 'react';
import {
  ListRenderItemInfo,
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
  Text,
  TouchableOpacity,
  TextInput,
  FlatList,
  ScrollView,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  NativeScrollEvent,
  TextStyle,
} from 'react-native';
import searchUtils from './searchUtils';
import { MB_Tag } from './types';

interface MB_TagsRef {
  addTag: (tag: MB_Tag) => void;
}

export enum TAG_THEMES {
  MAIN = 'MAIN',
  ALT = 'ALT',
}

interface MB_Tags {
  MB_Ref?: React.RefObject<MB_TagsRef>;
  title?: string;
  titleStyle?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  subContainerStyle?: StyleProp<ViewStyle>;
  tagPickerStyle?: StyleProp<ViewStyle>;
  textInputStyle?: StyleProp<TextStyle>;
  tagStyle?: StyleProp<ViewStyle>;
  tagHoverStyle?: StyleProp<ViewStyle>;
  tagTextStyle?: StyleProp<TextStyle>;
  tagBadgeStyle?: StyleProp<ViewStyle>;
  tagBadgeTextStyle?: StyleProp<TextStyle>;
  tagBadgeX?: {
    color?: string;
    size?: number;
  };
  emptyTagsTextStyle?: StyleProp<TextStyle>;
  itemSeparatorStyle?: StyleProp<ViewStyle>;
  initialTags?: MB_Tag[];
  onTagsChange?: (tags: MB_Tag[]) => void;
  tagTheme?: TAG_THEMES;
  titleButton?: () => React.JSX.Element;
  tags?: MB_Tag[];
  maxTagsLength?: number;
  maxTagLength?: number;
  disableAddNewTag?: boolean;
}

interface MouseDetectorProps {
  onMouseEnter?: (e: any) => void;
  onMouseLeave?: (e: any) => void;
  onMouseMove?: (e: any) => void;
  children: ReactElement;
}

const MouseDetector = ({ children, onMouseEnter, onMouseLeave, onMouseMove }: MouseDetectorProps) => {
  return React.cloneElement(React.Children.only(children), { onMouseEnter, onMouseLeave, onMouseMove });
};

const MB_Tags = ({
  MB_Ref,
  title,
  titleStyle,
  containerStyle,
  textInputStyle,
  subContainerStyle,
  tagPickerStyle,
  tagStyle,
  tagHoverStyle,
  tagTextStyle,
  tagBadgeStyle,
  tagBadgeTextStyle,
  tagBadgeX,
  itemSeparatorStyle,
  emptyTagsTextStyle,
  initialTags,
  tags,
  onTagsChange,
  tagTheme,
  titleButton,
  maxTagsLength = 10,
  maxTagLength,
  disableAddNewTag,
}: MB_Tags) => {
  const [tagsArray, setTagsArray] = useState<MB_Tag[]>(initialTags ?? []);
  const [tagText, setTagText] = useState('');
  const textInputRef = useRef<TextInput>(null);
  const [isTagPickerVisible, setIsTagPickerVisible] = useState(false);
  const [tagPickerTags, setTagPickerTags] = useState<MB_Tag[]>([]);
  const [isTagPickerAddButtonVisible, setIsTagPickerAddButtonVisible] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState<number>(-1);
  const flatListRef = useRef<FlatList<MB_Tag>>(null);

  const scrollOffset = useRef(0);
  const lastTimeStamp = useRef(Date.now());

  useImperativeHandle(MB_Ref, () => ({
    addTag: (tag: MB_Tag) => {
      if (!tagsArray.map(tg => tg.title).includes(tag.title)) {
        setTagsArray(old => [...old, tag]);
      }
    },
  }));

  useEffect(() => {
    if (tagsArray && tagsArray.length >= maxTagsLength) {
      textInputRef.current?.blur();
    }
    onTagsChange?.(tagsArray ?? []);
  }, [maxTagsLength, onTagsChange, tagsArray]);

  const onAddTag = useCallback((picketTag: MB_Tag) => {
    setTagText('');
    setIsTagPickerVisible(false);
    setTagsArray(old => [...(old ?? []), picketTag]);
  }, []);

  const onAddNewTag = useCallback(() => {
    setTagText('');
    setIsTagPickerVisible(false);
    setTagsArray(old => [...(old ?? []), { title: tagText }]);
  }, [tagText]);

  const onDeleteTag = useCallback((tag: MB_Tag) => {
    setTagsArray(old =>
      [...(old ?? [])].filter(oldTag => {
        if (oldTag._id && tag._id) {
          return oldTag._id !== tag._id;
        }
        return oldTag.title !== tag.title;
      })
    );
  }, []);

  const Tag = useMemo(
    () =>
      ({ tag }: { tag: MB_Tag }) => {
        return (
          <View
            style={[styles.tagContainer, { backgroundColor: tagTheme === TAG_THEMES.ALT ? '#5D3E65' : '#4D2D52', borderWidth: tagTheme === TAG_THEMES.ALT ? 1 : 0 }, tagBadgeStyle]}
          >
            <Text style={[styles.tag, tagBadgeTextStyle]} numberOfLines={1}>
              {tag.title}
            </Text>
            <TouchableOpacity style={styles.tagCloseButton} onPress={() => onDeleteTag(tag)}>
              <MB_SVG_X size={tagBadgeX?.size ?? 11} color={tagBadgeX?.color ?? '#BAC0CA'} />
            </TouchableOpacity>
          </View>
        );
      },
    [onDeleteTag, tagBadgeStyle, tagBadgeTextStyle, tagBadgeX?.color, tagBadgeX?.size, tagTheme]
  );

  const onChangeText = useCallback(
    (currentTagText: string) => {
      if (tagsArray && tagsArray.length < maxTagsLength) {
        setTagText(currentTagText);
        let matchingTags = searchUtils.tagSearch(tags ?? [], currentTagText, tagsArray);
        setTagPickerTags(matchingTags.map(matchingTag => matchingTag.item));

        // check if tag already added or disableAddNewTag is true
        const isAlreadyAdded = tagsArray?.find(tag => tag.title === currentTagText);
        if (isAlreadyAdded || disableAddNewTag) {
          setIsTagPickerAddButtonVisible(false);
          setHighlightedItem(0);
        } else {
          setIsTagPickerAddButtonVisible(true);
          setHighlightedItem(-1);
        }
        setIsTagPickerVisible(currentTagText !== '');
      } else {
        textInputRef.current?.blur();
      }
    },
    [disableAddNewTag, maxTagsLength, tags, tagsArray]
  );

  const onTextInputFocus = useCallback(() => onChangeText(tagText), [onChangeText, tagText]);

  const onTextInputBlur = useCallback(() => setTimeout(() => setIsTagPickerVisible(false), 100), []);

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffset.current = e.nativeEvent.contentOffset.y;
  }, []);

  const onKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Date.now() - lastTimeStamp.current < 100) {
        return;
      }
      lastTimeStamp.current = Date.now();
      const key = e.nativeEvent.key;
      const pad = 100 + (isTagPickerAddButtonVisible ? 0 : 34);
      if (key === 'ArrowDown') {
        e.preventDefault();
        const nextHighlightedItem = highlightedItem < tagPickerTags.length - 1 ? highlightedItem + 1 : highlightedItem;
        setHighlightedItem(nextHighlightedItem);
        const offset = 34 * nextHighlightedItem;
        if (!(offset > scrollOffset.current && offset < scrollOffset.current + pad)) {
          flatListRef.current?.scrollToOffset({ offset: offset - pad, animated: false });
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        const prevHighlightedItem = highlightedItem > 0 ? highlightedItem - 1 : !isTagPickerAddButtonVisible ? 0 : -1;
        setHighlightedItem(prevHighlightedItem);
        const offset = 34 * prevHighlightedItem;
        if (!(offset > scrollOffset.current && offset < scrollOffset.current + pad)) {
          flatListRef.current?.scrollToOffset({ offset, animated: false });
        }
      } else if (key === 'Enter') {
        e.preventDefault();
        if (highlightedItem >= 0 && tagPickerTags[highlightedItem]) {
          onAddTag(tagPickerTags[highlightedItem]);
        } else if (isTagPickerAddButtonVisible) {
          onAddNewTag();
        }
      } else if (key === 'Backspace') {
        if (tagText === '' && tagsArray.length > 0) {
          onDeleteTag(tagsArray[tagsArray.length - 1]);
        }
      }
    },
    [highlightedItem, isTagPickerAddButtonVisible, onAddNewTag, onAddTag, onDeleteTag, tagPickerTags, tagText, tagsArray]
  );

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<MB_Tag>) => (
      <MouseDetector onMouseMove={() => setHighlightedItem(index)}>
        <TouchableOpacity
          onPressIn={() => onAddTag(item)}
          style={[styles.tagPickerTagContainer, index === highlightedItem && { backgroundColor: '#452A4C' }, tagStyle, index === highlightedItem && tagHoverStyle]}
        >
          <Text style={[styles.itemOption, tagTextStyle]}>{item.title}</Text>
          {item.count !== undefined && (
            <Text style={styles.tagCount}>
              {item.count}
              <Text style={{ color: '#BAC0CA' }}> Uses</Text>
            </Text>
          )}
        </TouchableOpacity>
      </MouseDetector>
    ),
    [highlightedItem, onAddTag, tagHoverStyle, tagStyle, tagTextStyle]
  );

  const ListEmptyComponent = useCallback(
    () => (
      <View style={styles.listEmptyContainer}>
        <Text style={[styles.noResultsFound, emptyTagsTextStyle]}>No results found</Text>
      </View>
    ),
    [emptyTagsTextStyle]
  );

  const ListHeaderComponent = useCallback(
    () =>
      isTagPickerAddButtonVisible ? (
        <MouseDetector onMouseMove={() => setHighlightedItem(-1)}>
          <TouchableOpacity style={[styles.tagPickerAddNewTagContainer, highlightedItem === -1 && { backgroundColor: '#452A4C' }]} onPressIn={onAddNewTag}>
            <MB_SVG_Plus size={10} color="#FFFFFF" />
            <Text style={styles.createOption} numberOfLines={1}>
              Create a new option named
              <Text style={{ color: '#FFFFFF', fontWeight: 'bold' }}> {tagText}</Text>
            </Text>
          </TouchableOpacity>
        </MouseDetector>
      ) : null,
    [highlightedItem, isTagPickerAddButtonVisible, onAddNewTag, tagText]
  );

  const keyExtractor = useCallback((item: MB_Tag, index) => item._id ?? `${item.title}-${index}`, []);

  const ItemSeparatorComponent = useCallback(() => <View style={itemSeparatorStyle} />, [itemSeparatorStyle]);

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={[styles.title, titleStyle]}>{title ?? `Add Tags (Max ${maxTagsLength} tags)`}</Text>
        {titleButton?.()}
      </View>
      {tagsArray && (
        <TouchableOpacity
          // On web the ref is a DOM node without setNativeProps; calling it unguarded crashes the whole screen.
          ref={ref => ref?.setNativeProps?.({ tabIndex: -1 })}
          activeOpacity={1}
          style={[styles.tagsContainer, subContainerStyle]}
          onPress={() => textInputRef.current?.focus()}
        >
          <ScrollView>
            <View style={styles.tagsInnerContainer}>
              {tagsArray.map((tag, index) => (
                <Tag key={tag._id ?? `${tag.title}-${index}`} tag={tag} />
              ))}
              <TextInput
                ref={textInputRef}
                style={[styles.textInput, textInputStyle]}
                value={tagText}
                placeholder="Enter tag name"
                onChangeText={onChangeText}
                onKeyPress={onKeyPress}
                maxLength={maxTagLength}
                onFocus={onTextInputFocus}
                onBlur={onTextInputBlur}
              />
            </View>
          </ScrollView>
        </TouchableOpacity>
      )}
      {isTagPickerVisible && (
        <View style={[styles.tagPicker, tagPickerStyle]}>
          <FlatList
            ref={flatListRef}
            data={tagPickerTags}
            style={styles.list}
            onScroll={onScroll}
            renderItem={renderItem}
            stickyHeaderIndices={[0]}
            ListEmptyComponent={ListEmptyComponent}
            ListHeaderComponent={ListHeaderComponent}
            keyExtractor={keyExtractor}
            ItemSeparatorComponent={ItemSeparatorComponent}
          />
        </View>
      )}
    </View>
  );
};

export { MB_Tags };
export type { MB_TagsRef };

const styles = StyleSheet.create({
  container: {
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    backgroundColor: '#1d1a31',
  },
  list: {
    height: 173,
  },
  tagsContainer: {
    paddingHorizontal: 12,
    paddingTop: 14,
    borderRadius: 6,
    minHeight: 60,
    borderColor: '#BAC0CA',
    borderWidth: 1,
    cursor: 'text',
  },
  tagsInnerContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  textInput: {
    flex: 1,
    textAlign: 'left',
    height: 30,
    minWidth: 50,
    paddingVertical: 0,
    lineHeight: undefined,
    fontSize: 14,
    color: '#FFFFFF',
    ...mbPlatformStyle({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  tagContainer: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#5D3E65',
    borderRadius: 4,
    height: 30,
    marginBottom: 14,
    marginEnd: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderColor: mbApplyTransparency('#FFFFFF', 0.15),
    cursor: 'default',
    maxWidth: '100%',
  },
  tagCloseButton: {
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingStart: 10,
  },
  tagPicker: {
    position: 'absolute',
    top: '100%',
    width: '100%',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    backgroundColor: '#5D3E65',
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
    borderColor: mbApplyTransparency('#FFFFFF', 1),
    maxHeight: 173,
    marginTop: 8,
  },
  tagPickerTagContainer: {
    height: 34,
    borderColor: mbApplyTransparency('#BAC0CA', 0.1),
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tagPickerAddNewTagContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderColor: mbApplyTransparency('#BAC0CA', 0.04),
    borderBottomWidth: 1,
    flex: 1,
    backgroundColor: '#5D3E65',
  },
  tagCount: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: mbApplyTransparency('#000000', 0.25),
    borderRadius: 6,
    fontSize: 12,
    textAlign: 'center',
  },
  title: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 28,
    color: '#FFFFFF',
    textAlign: 'left',
  },
  tag: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    lineHeight: 18,
    textAlign: 'left',
    flex: 1,
  },
  listEmptyContainer: {
    justifyContent: 'center',
    height: 171,
  },
  noResultsFound: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    textAlign: 'center',
    marginStart: 18,
    lineHeight: 34,
  },
  createOption: {
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    color: '#BAC0CA',
    fontWeight: '400',
    marginStart: 4,
    flex: 1,
  },
  itemOption: {
    fontSize: 12,
    color: '#FFFFFF',
    lineHeight: 18,
    textAlign: 'left',
  },
});
